import { useState, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Loader2,
  Eye,
  EyeOff,
  ExternalLink,
  AlertCircle,
  Copy,
  Check,
} from 'lucide-react'
import { useLineMessagingConfig } from './useLineMessagingConfig'
import { Link } from 'react-router-dom'

// LINE icon SVG (official brand mark)
function LineIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  )
}

// Shared preview wrapper — mimics LINE chat bubble
function BubbleWrap({ children, footer }: { children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="bg-gray-100 rounded-lg p-3">
      <div className="max-w-[280px] mx-auto bg-white rounded-xl shadow-sm overflow-hidden text-[13px] leading-[1.4]">
        {children}
        {footer && (
          <>
            <div className="border-t border-[#eee]" />
            {footer}
          </>
        )}
      </div>
    </div>
  )
}

// Item row with thumbnail placeholder — used in order/payment/reminder
function ItemRowWithImage({ name, qty, price }: { name: string; qty: number; price?: string }) {
  const text = qty > 1 ? `${name} x${qty}` : name
  return (
    <div className="flex items-center gap-2">
      <div className="w-[32px] h-[32px] rounded bg-gray-100 shrink-0" />
      <p className="flex-[4] min-w-0 text-[#111] text-[13px] break-words">{text}</p>
      {price && <span className="flex-[2] text-[#111] text-[13px] text-right shrink-0">{price}</span>}
    </div>
  )
}

// Totals row — muted label left, value right
function TotalsRow({ label, value, bold, color }: { label: string; value: string; bold?: boolean; color?: string }) {
  return (
    <div className="flex justify-between">
      <span className={`${bold ? 'font-bold' : ''}`} style={{ color: color || (bold ? '#111' : '#999'), flex: 4, fontSize: bold ? 14 : 13 }}>{label}</span>
      <span className={`text-right ${bold ? 'font-bold' : ''}`} style={{ color: color || '#111', flex: 3, fontSize: bold ? 14 : 13 }}>{value}</span>
    </div>
  )
}

function OrderConfirmationPreview() {
  return (
    <BubbleWrap
      footer={
        <div className="p-3">
          <div className="bg-[#111] text-white text-center py-2 rounded-md font-medium text-[13px]">
            Complete Payment
          </div>
        </div>
      }
    >
      <div className="px-4 pt-4 space-y-0.5">
        <p className="font-bold text-[15px] text-[#111]">Your Order</p>
        <p className="text-[#999] text-[12px]">SO-2026-00001</p>
      </div>
      {/* Urgency note for Bank Transfer */}
      <div className="px-4 pt-2">
        <p className="text-[#999] text-[12px] italic text-center">Please complete payment within 24 hours</p>
      </div>
      {/* Delivery section */}
      <div className="border-t border-[#eee] mx-4 mt-3" />
      <div className="px-4 pt-3 space-y-0.5">
        <p className="text-[#111] text-[13px] font-bold">Delivery</p>
        <p className="text-[#111] text-[13px]">John Smith</p>
        <p className="text-[#999] text-[12px]">123 Sukhumvit Rd, Bangkok 10110</p>
        <p className="text-[#999] text-[12px]">via Standard Delivery</p>
      </div>
      {/* Items */}
      <div className="border-t border-[#eee] mx-4 mt-3" />
      <div className="px-4 pt-3 pb-1 space-y-2">
        <ItemRowWithImage name="Sample Product" qty={2} price="THB 590" />
        <ItemRowWithImage name="Another Item" qty={1} price="THB 350" />
      </div>
      {/* Totals */}
      <div className="border-t border-[#eee] mx-4 mt-2" />
      <div className="px-4 py-3 space-y-1">
        <TotalsRow label="Subtotal" value="THB 1,530" />
        <TotalsRow label="Shipping" value="THB 50" />
        <TotalsRow label="Total" value="THB 1,580" bold />
      </div>
    </BubbleWrap>
  )
}

function PaymentConfirmationPreview() {
  return (
    <BubbleWrap
      footer={
        <div className="p-3">
          <div className="bg-[#111] text-white text-center py-2 rounded-md font-medium text-[13px]">
            View Order
          </div>
        </div>
      }
    >
      <div className="px-4 pt-4 space-y-0.5">
        <p className="font-bold text-[15px] text-[#22C55E]">Payment Received!</p>
        <p className="text-[#999] text-[12px]">SO-2026-00001</p>
      </div>
      <div className="px-4 pt-2">
        <p className="text-[#999] text-[13px]">
          Great news! We've received your payment and your order is now being prepared.
        </p>
      </div>
      {/* Items with thumbnails (no prices) */}
      <div className="border-t border-[#eee] mx-4 mt-3" />
      <div className="px-4 pt-3 pb-1 space-y-2">
        <ItemRowWithImage name="Sample Product" qty={2} />
        <ItemRowWithImage name="Another Item" qty={1} />
      </div>
      {/* Total Paid */}
      <div className="border-t border-[#eee] mx-4 mt-2" />
      <div className="px-4 py-3">
        <div className="flex justify-between">
          <span className="font-bold text-[14px] text-[#111]" style={{ flex: 4 }}>Total Paid</span>
          <span className="font-bold text-[14px] text-[#22C55E] text-right" style={{ flex: 3 }}>THB 1,580</span>
        </div>
      </div>
      <div className="px-4 pb-3">
        <p className="text-[#999] text-[12px]">We'll notify you once your order ships. Thank you!</p>
      </div>
    </BubbleWrap>
  )
}

function PaymentReminderPreview() {
  return (
    <BubbleWrap
      footer={
        <div className="p-3">
          <div className="bg-[#111] text-white text-center py-2 rounded-md font-medium text-[13px]">
            Complete Your Order
          </div>
        </div>
      }
    >
      <div className="px-4 pt-4 space-y-0.5">
        <p className="font-bold text-[14px] text-[#111]">Your order is waiting!</p>
        <p className="text-[#999] text-[12px]">SO-2026-00001</p>
      </div>
      <div className="px-4 pt-2">
        <p className="text-[#999] text-[13px]">
          Just a friendly reminder — complete your payment so we can ship your items.
        </p>
      </div>
      {/* Items with thumbnails AND prices */}
      <div className="border-t border-[#eee] mx-4 mt-3" />
      <div className="px-4 pt-3 pb-1 space-y-2">
        <ItemRowWithImage name="Sample Product" qty={2} price="THB 590" />
        <ItemRowWithImage name="Another Item" qty={1} price="THB 350" />
      </div>
      {/* Total */}
      <div className="border-t border-[#eee] mx-4 mt-2" />
      <div className="px-4 py-3">
        <TotalsRow label="Total" value="THB 1,580" bold />
      </div>
    </BubbleWrap>
  )
}

function ShipmentTrackingPreview() {
  return (
    <BubbleWrap
      footer={
        <div className="p-3">
          <div className="bg-[#0151FE] text-white text-center py-2 rounded-md font-medium text-[13px]">
            Track Order
          </div>
        </div>
      }
    >
      {/* Header — separate section like the backend */}
      <div className="px-4 pt-4 pb-0">
        <p className="font-bold text-[14px] text-[#06C755]">Your order has shipped!</p>
      </div>
      {/* Separator + Info rows (flex:2 label, flex:4 value) */}
      <div className="border-t border-[#eee] mx-4 mt-3" />
      <div className="px-4 pt-3 space-y-1.5">
        <div className="flex">
          <span className="text-[#999] text-[13px]" style={{ flex: 2 }}>Order</span>
          <span className="text-[#111] text-[13px] font-bold" style={{ flex: 4 }}>SO-2026-00001</span>
        </div>
        <div className="flex">
          <span className="text-[#999] text-[13px]" style={{ flex: 2 }}>Tracking</span>
          <span className="text-[#111] text-[13px] font-bold break-all" style={{ flex: 4 }}>TH123456789</span>
        </div>
        <div className="flex">
          <span className="text-[#999] text-[13px]" style={{ flex: 2 }}>Carrier</span>
          <span className="text-[#111] text-[13px] font-bold" style={{ flex: 4 }}>Thailand Post</span>
        </div>
      </div>
      {/* Separator + Items (name flex:4 + qty flex:1, no thumbnails, no prices) */}
      <div className="border-t border-[#eee] mx-4 mt-3" />
      <div className="px-4 pt-3 pb-3 space-y-1">
        <p className="text-[#111] text-[13px] font-bold">Items</p>
        <div className="flex">
          <span className="text-[#111] text-[13px]" style={{ flex: 4 }}>Sample Product</span>
          <span className="text-[#999] text-[13px] text-right" style={{ flex: 1 }}>x2</span>
        </div>
        <div className="flex">
          <span className="text-[#111] text-[13px]" style={{ flex: 4 }}>Another Item</span>
          <span className="text-[#999] text-[13px] text-right" style={{ flex: 1 }}>x1</span>
        </div>
      </div>
    </BubbleWrap>
  )
}

function BookingConfirmationPreview() {
  return (
    <BubbleWrap
      footer={
        <div className="p-3">
          <div className="bg-[#0EA5E9] text-white text-center py-2 rounded-md font-medium text-[13px]">
            ดูรายละเอียดการจอง
          </div>
        </div>
      }
    >
      <div className="px-4 pt-4 space-y-0.5 text-center">
        <p className="font-bold text-[15px] text-[#111]">✅ จองสำเร็จ!</p>
        <p className="text-[#999] text-[12px]">หมายเลขจอง #BK-2026-00001</p>
      </div>
      {/* Info rows — flex 3:5 matching _info_row() */}
      <div className="border-t border-[#eee] mx-4 mt-3" />
      <div className="px-4 pt-3 space-y-1.5">
        <div className="flex">
          <span className="text-[#999] text-[13px]" style={{ flex: 3 }}>สนาม</span>
          <span className="text-[#111] text-[13px] break-words" style={{ flex: 5 }}>สนามฟุตบอล A</span>
        </div>
        <div className="flex">
          <span className="text-[#999] text-[13px]" style={{ flex: 3 }}>วันที่</span>
          <span className="text-[#111] text-[13px] break-words" style={{ flex: 5 }}>15 ก.ค. 2569</span>
        </div>
        <div className="flex">
          <span className="text-[#999] text-[13px]" style={{ flex: 3 }}>เวลา</span>
          <span className="text-[#111] text-[13px] break-words" style={{ flex: 5 }}>18:00 - 19:00</span>
        </div>
        <div className="flex">
          <span className="text-[#999] text-[13px]" style={{ flex: 3 }}>ชื่อ</span>
          <span className="text-[#111] text-[13px] break-words" style={{ flex: 5 }}>สมชาย ใจดี</span>
        </div>
      </div>
      {/* Price row */}
      <div className="border-t border-[#eee] mx-4 mt-3" />
      <div className="px-4 py-3">
        <div className="flex justify-between">
          <span className="text-[#999] text-[13px]" style={{ flex: 3 }}>ราคา</span>
          <span className="font-bold text-[15px] text-[#111] text-right" style={{ flex: 4 }}>฿1,200</span>
        </div>
      </div>
    </BubbleWrap>
  )
}

const PREVIEW_MAP: Record<string, () => JSX.Element> = {
  enable_line_order_confirmation: OrderConfirmationPreview,
  enable_line_payment_confirmation: PaymentConfirmationPreview,
  enable_line_payment_reminder: PaymentReminderPreview,
  enable_line_shipment_notification: ShipmentTrackingPreview,
}

function LineMessagePreview({ type }: { type: string }) {
  const Preview = PREVIEW_MAP[type]
  if (!Preview) return null
  return <Preview />
}

// Small read-only field with a copy button — used for the Webhook URL
function CopyableField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(() => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }, [value])
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-2">
        <Input value={value} readOnly className="rounded font-mono text-xs" onFocus={(e) => e.target.select()} />
        <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0 min-w-[84px]">
          {copied ? <Check className="w-3.5 h-3.5 mr-1 text-green-600" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function LineMessagingSettings() {
  const {
    status,
    isLoading,
    connect,
    isConnecting,
    connectByCredentials,
    isConnectingByCredentials,
    connectByCredentialsError,
    disconnect,
    isDisconnecting,
    connectError,
    updatePreferences,
    isUpdatingPreferences,
  } = useLineMessagingConfig()

  const [imgError, setImgError] = useState(false)
  const [tokenInput, setTokenInput] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [showChangeToken, setShowChangeToken] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  // Self-serve connect: Channel ID + Secret (primary), token paste (advanced)
  const [connectMode, setConnectMode] = useState<'credentials' | 'token'>('credentials')
  const [channelId, setChannelId] = useState('')
  const [channelSecret, setChannelSecret] = useState('')
  const [showSecret, setShowSecret] = useState(false)

  const resetForms = useCallback(() => {
    setTokenInput('')
    setChannelId('')
    setChannelSecret('')
    setShowChangeToken(false)
  }, [])

  const handleConnectCredentials = useCallback(() => {
    if (!channelId.trim() || !channelSecret.trim()) return
    connectByCredentials(
      { channelId: channelId.trim(), channelSecret: channelSecret.trim() },
      { onSuccess: resetForms }
    )
  }, [channelId, channelSecret, connectByCredentials, resetForms])

  const handleConnect = useCallback(() => {
    if (!tokenInput.trim()) return
    connect(tokenInput.trim(), {
      onSuccess: () => {
        setTokenInput('')
        setShowChangeToken(false)
      },
    })
  }, [tokenInput, connect])

  const handleDisconnect = useCallback(() => {
    disconnect(undefined, {
      onSuccess: () => {
        setShowDisconnectConfirm(false)
      },
    })
  }, [disconnect])

  if (isLoading) {
    return (
      <Card className="rounded-2xl border-0">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  const isConnected = status?.connected

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LineIcon className="w-4 h-4 text-[#06C755]" />
            <span className="font-semibold">LINE Messaging API</span>
          </div>
          <Badge
            className={
              isConnected
                ? 'bg-green-50 text-green-700 border-0'
                : 'bg-gray-100 text-gray-600 border-0'
            }
          >
            {isConnected ? 'Connected' : 'Not connected'}
          </Badge>
        </div>

        {isConnected && !showChangeToken ? (
          <>
            {/* Connected state — show bot info */}
            <div className="flex items-center gap-3">
              {status.bot_picture_url && !imgError ? (
                <img
                  src={status.bot_picture_url}
                  alt={status.bot_display_name}
                  className="w-10 h-10 rounded-full"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#06C755] flex items-center justify-center">
                  <LineIcon className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <p className="font-medium">{status.bot_display_name}</p>
                <p className="text-xs text-muted-foreground">
                  {status.channel_id ? 'Token active — auto-refreshed' : 'Token saved'}
                </p>
              </div>
            </div>

            {/* Webhook URL — paste into the LINE Developers console to receive replies */}
            {status.webhook_url && (
              <div className="border-t border-gray-100 pt-4">
                <CopyableField
                  label="Webhook URL"
                  value={status.webhook_url}
                  hint="Paste this into your Messaging API channel → Webhook URL in the LINE Developers Console, then turn on “Use webhook” to receive customer replies in your inbox."
                />
              </div>
            )}

            {/* Notification cards */}
            <div className="border-t border-gray-100 pt-4 space-y-4">
              <p className="text-sm font-medium text-gray-700">Notifications</p>
              {([
                {
                  key: 'enable_line_order_confirmation',
                  label: 'Order confirmation',
                  desc: 'Sent when a new order is placed',
                },
                {
                  key: 'enable_line_payment_confirmation',
                  label: 'Payment confirmation',
                  desc: 'Sent when payment is received',
                },
                {
                  key: 'enable_line_payment_reminder',
                  label: 'Payment reminder',
                  desc: 'Sent when an order is awaiting payment',
                },
                {
                  key: 'enable_line_shipment_notification',
                  label: 'Shipment tracking',
                  desc: 'Sent when an order has been shipped',
                },
              ] as const).map(({ key, label, desc }) => (
                <div key={key} className="rounded-lg border border-gray-100 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-[#111]">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                    <Switch
                      checked={!!status?.[key]}
                      disabled={isUpdatingPreferences}
                      onCheckedChange={(checked) =>
                        updatePreferences({ [key]: checked ? 1 : 0 })
                      }
                    />
                  </div>
                  <LineMessagePreview type={key} />
                </div>
              ))}

              {/* Booking confirmation — always on, no toggle */}
              <div className="rounded-lg border border-gray-100 p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#111]">Booking confirmation</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Sent automatically when a booking is created</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">Always on</Badge>
                </div>
                <BookingConfirmationPreview />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowChangeToken(true)}
              >
                Reconnect
              </Button>
              {showDisconnectConfirm ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Disconnect?</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDisconnect}
                    disabled={isDisconnecting}
                  >
                    {isDisconnecting ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : null}
                    Yes, disconnect
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDisconnectConfirm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setShowDisconnectConfirm(true)}
                >
                  Disconnect
                </Button>
              )}
            </div>

            {/* Link to LINE OA Dashboard */}
            <Link
              to="/line-oa"
              className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              Go to LINE OA Dashboard
              <ExternalLink className="w-3 h-3" />
            </Link>
          </>
        ) : (
          <>
            {/* Not connected state (or changing token) */}
            <p className="text-sm text-muted-foreground">
              Connect your LINE Messaging API to send order updates, payment reminders,
              and shipment tracking to your customers — and receive their replies in your inbox.
            </p>

            {showChangeToken && (
              <div className="flex items-center gap-2 p-2 rounded-sm bg-amber-50 text-amber-700 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>Reconnecting will replace the current credentials.</span>
              </div>
            )}

            {connectMode === 'credentials' ? (
              <>
                {/* Channel ID + Secret — primary self-serve flow (auto-mints token) */}
                <div className="space-y-1.5">
                  <Label htmlFor="line-channel-id" className="text-sm">
                    Channel ID
                  </Label>
                  <Input
                    id="line-channel-id"
                    inputMode="numeric"
                    placeholder="e.g. 2007442469"
                    value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}
                    disabled={isConnectingByCredentials}
                    className={`rounded ${connectByCredentialsError ? 'border-red-500' : ''}`}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="line-channel-secret" className="text-sm">
                    Channel Secret
                  </Label>
                  <div className="relative">
                    <Input
                      id="line-channel-secret"
                      type={showSecret ? 'text' : 'password'}
                      placeholder="Paste your Channel Secret here"
                      value={channelSecret}
                      onChange={(e) => setChannelSecret(e.target.value)}
                      disabled={isConnectingByCredentials}
                      className={`rounded pr-10 ${connectByCredentialsError ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    From your <strong>Messaging API</strong> channel (not LINE Login) → Basic settings.
                    We generate and auto-refresh the access token for you, so it never expires.{' '}
                    <a
                      href="https://developers.line.biz/console/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
                    >
                      LINE Developer Console
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>

                {connectByCredentialsError && (
                  <p className="text-sm text-red-600" role="alert">
                    {connectByCredentialsError}
                  </p>
                )}

                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setConnectMode('token')}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Use a Channel Access Token instead
                  </button>
                  <div className="flex gap-2">
                    {showChangeToken && (
                      <Button variant="ghost" onClick={resetForms}>
                        Cancel
                      </Button>
                    )}
                    <Button
                      onClick={handleConnectCredentials}
                      disabled={isConnectingByCredentials || !channelId.trim() || !channelSecret.trim()}
                      className="min-w-[100px]"
                    >
                      {isConnectingByCredentials ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                          Connecting...
                        </>
                      ) : (
                        'Connect'
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Advanced: paste a long-lived Channel Access Token directly */}
                <div className="space-y-1.5">
                  <Label htmlFor="line-messaging-token" className="text-sm">
                    Channel Access Token
                  </Label>
                  <div className="relative">
                    <Input
                      id="line-messaging-token"
                      type={showToken ? 'text' : 'password'}
                      placeholder="Paste your Channel Access Token here"
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      disabled={isConnecting}
                      className={`rounded pr-10 ${connectError ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowToken(!showToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showToken ? 'Hide token' : 'Show token'}
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A long-lived token from the <strong>Messaging API</strong> channel. This won&apos;t
                    auto-refresh — use Channel ID + Secret if you want that.
                  </p>
                </div>

                {connectError && (
                  <p className="text-sm text-red-600" role="alert">
                    {connectError}
                  </p>
                )}

                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setConnectMode('credentials')}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Use Channel ID + Secret instead
                  </button>
                  <div className="flex gap-2">
                    {showChangeToken && (
                      <Button variant="ghost" onClick={resetForms}>
                        Cancel
                      </Button>
                    )}
                    <Button
                      onClick={handleConnect}
                      disabled={isConnecting || !tokenInput.trim()}
                      className="min-w-[100px]"
                    >
                      {isConnecting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                          Connecting...
                        </>
                      ) : (
                        'Connect'
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
